import React, { useState, useEffect } from 'react';
import { Icon } from '../../components/Icon';
import { Table } from './Table';
import { useDeal } from '../../hooks/useDeals';
import { DealFormCard } from '../../components/DealFormCard';
import { DealsSearchFilters, type DealFilters } from '../../components/DealsSearchFilters';

const Deals: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [personType] = useState("");
    const [showLeadForm, setShowLeadForm] = useState(false);
    const [filters, setFilters] = useState<DealFilters>({
        searchTerm: '',
        agentId: '',
        sourceId: '',
        statusId: ''
    });

    const handleOpenForm = () => {
        setShowLeadForm(prev => !prev);
    };

    const handleCloseForm = () => {
        setShowLeadForm(false);
    };

    const {
        data: deals,
        statusCount,
        loading,
        error,
        total,
        totalPages,
        refetch,
        creating,
        createDeal
    } = useDeal({
        tipo_persona_id: personType,
        page: currentPage,
        pageSize,
        searchTerm: filters.searchTerm,
        agentId: filters.agentId,
        sourceId: filters.sourceId,
        statusId: filters.statusId
    });

    // Reset page when search or filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters.searchTerm, filters.agentId, filters.sourceId, filters.statusId]);

    const handleFiltersChange = (newFilters: DealFilters) => {
        setFilters(newFilters);
    };

    const handleSearch = () => {
        refetch();
    };

    if (error) {
        return (
            <div className="space-y-6 animate-fade-in">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Gestión de Deals
                    </h2>
                </div>
                <div className="card p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <div className="flex items-center">
                        <Icon name="alert-circle" className="w-5 h-5 text-red-500 mr-2" />
                        <span className="text-red-700 dark:text-red-300">
                            Error: {error}
                        </span>
                    </div>
                    <button
                        onClick={refetch}
                        className="mt-3 btn-primary"
                    >
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Gestión de Deals
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                        {total} deals encontrados
                    </p>
                </div>
                <div className="flex items-center space-x-3">


                    <button
                        onClick={handleOpenForm}
                        className={`flex items-center px-4 py-2 text-sm font-medium text-white ${showLeadForm ? "bg-red-400 hover:bg-red-500" : "bg-blue-600 hover:bg-blue-700"}  rounded-lg  transition-colors`}
                    >
                        {showLeadForm ? (
                            <>
                                <Icon name="close" className="w-4 h-4 mr-2" />
                                Cancelar
                            </>
                        ) : (
                            <>
                                <Icon name="plus" className="w-4 h-4 mr-2" />
                                Nuevo Deal
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className=" dark:bg-gray-800 rounded-lg">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
                    {statusCount.map((item, index) => (
                        item.deal_count > 0 && (
                            <div
                                key={index}
                                className="flex items-center space-x-2 bg-white dark:bg-gray-700 rounded-md border border-primary p-3"
                            >
                                <span className="text-xl font-bold text-primary dark:text-blue-400">
                                    {item.deal_count}
                                </span>
                                <span className="text-gray-700 dark:text-gray-300">{item.status_name}</span>
                            </div>
                        )
                    ))}
                </div>
            </div>



            {/* Componente hijo - Card del formulario */}
            <DealFormCard
                creating={creating}
                isVisible={showLeadForm}
                onClose={handleCloseForm}
                onSubmit={createDeal}
            />

            {/* Filtros y búsqueda */}
            <DealsSearchFilters
                onFiltersChange={handleFiltersChange}
                onSearch={handleSearch}
                loading={loading}
            />

            <Table
                data={deals}
                loading={loading}
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                total={total}
                onPageChange={setCurrentPage}
                onRefresh={refetch}
            />
        </div>
    );
};

export default Deals;